'use client';

import Image from 'next/image';
import { useRef, Fragment } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import landscape from '@/public/home/landscape.png';
import landscapeMobile from '@/public/home/landscapeMobile.png';

interface IAboutProps {
  title: PortableTextBlock[];
  titleMobile: {
    titleMobileOne: PortableTextBlock[];
    titleMobileTwo: PortableTextBlock[];
  };
}

export default function About({
  title,
  titleMobile,
}: IAboutProps) {
  const isMobile = useIsMobile();

  return (
    <section className="flex items-center justify-center w-full bg-[#072922]">
      {/* Desktop */}
      <div className="container lg:flex items-center justify-center w-full min-h-[106.25rem] relative hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.2 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.15, ease: easeOut }}
          viewport={{ once: true }}
          className="richtext richtext-center prose-headings:text-offwhite max-w-[183.125rem] relative z-10"
        >
          <PortableText value={title} components={richTextComponent} />
        </motion.div>
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-[59.5rem] aspect-[4/5]">
          <motion.div
            initial={{ opacity: 0, scale: 0.2 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.15, ease: easeOut }}
            viewport={{ once: true }}
            className="w-full h-full relative"
          >
            <svg className="absolute w-full h-full object-cover scale-[1.17] z-10" viewBox="0 0 560 688" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.17233e-06 -2.05444e-06L2.11789e-06 47L14.5942 47L14.5942 12.5603L47 12.5603L47 0L4.17233e-06 -2.05444e-06Z" fill="#B0F4B7"/>
              <path d="M560 688L560 641L545.406 641L545.406 675.44L513 675.44L513 688L560 688Z" fill="#B0F4B7"/>
            </svg>
            <Image
              src={landscape}
              className="w-full h-full object-cover"
              alt=""
              quality={100}
            />
          </motion.div>
        </div>
      </div>

      {/* Mobile */}
      <div className="container flex flex-col items-center justify-center w-full py-[10.05rem] gap-[3rem] relative lg:hidden">
        {titleMobile?.titleMobileOne?.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1.15, ease: easeOut }}
            viewport={{ once: true }}
            className="richtext richtext-center prose-headings:text-[4.5rem] prose-headings:text-offwhite"
          >
            <PortableText value={titleMobile?.titleMobileOne} components={richTextComponent} />
          </motion.div>
        )}
        <motion.div
          initial={{ opacity: 0, scale: 0.2 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.15, ease: easeOut }}
          viewport={{ once: true }}
          className="w-full h-auto relative"
        >
          <Image
            src={landscapeMobile}
            className="w-full h-full object-cover"
            alt=""
            quality={100}
          />
        </motion.div>
        {titleMobile?.titleMobileTwo?.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1.15, ease: easeOut }}
            viewport={{ once: true }}
            className="richtext richtext-center prose-headings:text-[4.5rem] prose-headings:text-offwhite"
          >
            <PortableText value={titleMobile?.titleMobileTwo} components={richTextComponent} />
          </motion.div>
        )}
      </div>
    </section>
  );
}
